// Ring primitive — flat torus on the XZ plane that expands from
// startRadius to endRadius over durationSec, fading out. Used for
// teleport/shield/construction-complete VFX.

import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import type { RingDef } from './types.js';

interface RingProps {
  readonly def: RingDef;
  readonly position: readonly [number, number, number];
}

export function Ring({ def, position }: RingProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const matRef = useRef<THREE.MeshBasicMaterial>(null);
  const startTimeRef = useRef(performance.now());

  // Build geometry at unit radius; we scale per-frame to interpolate.
  const geometry = useMemo(() => new THREE.TorusGeometry(1, 0.05, 8, 48), []);

  useFrame(() => {
    const mesh = meshRef.current;
    const mat = matRef.current;
    if (!mesh || !mat) return;
    const elapsed = (performance.now() - startTimeRef.current) / 1000;
    const t = Math.min(elapsed / def.durationSec, 1);
    const radius = def.startRadius + (def.endRadius - def.startRadius) * t;
    mesh.scale.set(radius, radius, radius);
    mat.opacity = 1 - t;
  });

  return (
    <mesh
      ref={meshRef}
      position={[position[0], position[1], position[2]]}
      rotation={[-Math.PI / 2, 0, 0]}
      geometry={geometry}
    >
      <meshBasicMaterial
        ref={matRef}
        color={def.color}
        transparent
        opacity={1}
        depthWrite={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}
